/**
 * Release governance primitives — shared FM + BH.
 * Build scripts populate product-specific generated files; UI reads via API or import.
 */

export type ReleaseChannel = "dev" | "rc" | "prod";

export type ReleaseStatus = "active" | "homologated" | "superseded" | "rolled_back";

/** Runtime/build snapshot of the running deploy. */
export interface ReleaseBuildInfo {
  product: "fm" | "bh";
  version: string;
  channel: ReleaseChannel;
  branch: string | null;
  commit: string | null;
  builtAt: string;
  /** Human label e.g. v0.15.0-rc1 */
  releaseLabel?: string | null;
  environment?: string | null;
}

export interface ReleaseRegistryEntry {
  id: string;
  product: "fm" | "bh" | "platform";
  version: string;
  channel: ReleaseChannel;
  branch: string;
  commit?: string | null;
  deployedAt?: string | null;
  builtAt?: string | null;
  status: ReleaseStatus;
  notes?: string;
  homologatedAt?: string | null;
}

export interface ReleaseRegistry {
  schemaVersion: number;
  updatedAt: string;
  pointers: {
    fm: { prod: string | null; latestRc: string | null; devBranch: string };
    bh: { prod: string | null; latestRc: string | null; devBranch: string };
  };
  releases: ReleaseRegistryEntry[];
}

export function inferChannelFromBranch(branch: string | null | undefined): ReleaseChannel {
  if (!branch) return "dev";
  const b = branch.toLowerCase();
  if (b === "main" || b === "master" || b === "production") return "prod";
  if (b.startsWith("release/") || b.includes("-rc")) return "rc";
  return "dev";
}

export function channelLabel(channel: ReleaseChannel): string {
  return channel.toUpperCase();
}

export function channelBadgeClass(channel: ReleaseChannel): string {
  switch (channel) {
    case "prod":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";
    case "rc":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30";
    default:
      return "bg-amber-500/15 text-amber-800 dark:text-amber-300 border-amber-500/30";
  }
}

export function formatBuiltAtUtc(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
}
